import { QueryClient } from '@tanstack/react-query';

export interface CacheData {
  configs: any[];
  lastUpdated: number;
}

class CacheService {
  private queryClient: QueryClient | null = null;
  private cacheKey = 'app-cache';

  constructor() {
    this.initializeFromStorage();
  }

  setQueryClient(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  // Initialize cache from localStorage on app start
  private initializeFromStorage() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (cached) {
        const data: CacheData = JSON.parse(cached);
        // Check if cache is not too old (24 hours)
        const isRecent = Date.now() - data.lastUpdated < 24 * 60 * 60 * 1000;
        if (isRecent && data.configs) {
          console.log(
            'Loaded configs from localStorage cache:',
            data.configs.length,
            'items'
          );
          return data;
        }
      }
    } catch (error) {
      console.warn('Failed to load cache from localStorage:', error);
    }
    return null;
  }

  // Load all user data into cache after login
  async loadUserDataOnLogin(queryClient: QueryClient) {
    console.log('Loading user data into cache...');

    try {
      // Actually fetch the data (not just prefetch)
      const { configAPI } = await import('@/lib/api');
      const configs = await configAPI.getAll();

      // Store in React Query cache with correct structure
      queryClient.setQueryData(['configs'], {
        data: {
          configs: configs,
          total: configs.length,
        },
      });

      // Store in localStorage for persistence
      this.saveToLocalStorage({
        configs: configs,
        lastUpdated: Date.now(),
      });
      console.log('User data cached successfully:', configs.length, 'configs');
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  }

  // Save data to localStorage for persistence
  private saveToLocalStorage(data: CacheData) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save cache to localStorage:', error);
    }
  }

  // Update cache when data changes
  updateCache(queryKey: string[], data: any) {
    if (!this.queryClient) return;

    // Update React Query cache
    this.queryClient.setQueryData(queryKey, data);

    // Update localStorage if it's configs data
    if (queryKey[0] === 'configs') {
      const currentCache = this.getCacheData();
      if (currentCache) {
        const updatedCache: CacheData = {
          ...currentCache,
          configs: data?.data?.configs || data,
          lastUpdated: Date.now(),
        };
        this.saveToLocalStorage(updatedCache);
        console.log('Cache updated for configs');
      }
    }
  }

  // Get cached data
  getCacheData(): CacheData | null {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }

  // Get specific config from cache
  getConfigFromCache(configId: string): any | null {
    const cacheData = this.getCacheData();
    if (cacheData?.configs) {
      return (
        cacheData.configs.find((config: any) => config._id === configId) || null
      );
    }
    return null;
  }

  // Update specific config in cache
  updateConfigInCache(updatedConfig: any) {
    const cacheData = this.getCacheData();
    if (cacheData?.configs) {
      const configIndex = cacheData.configs.findIndex(
        (config: any) => config._id === updatedConfig._id
      );
      if (configIndex !== -1) {
        cacheData.configs[configIndex] = updatedConfig;
        cacheData.lastUpdated = Date.now();

        // Update localStorage
        try {
          localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
        } catch (error) {
          console.warn('Failed to update localStorage cache:', error);
        }

        // Update React Query cache
        if (this.queryClient) {
          this.queryClient.setQueryData(['configs'], {
            data: {
              configs: cacheData.configs,
              total: cacheData.configs.length,
            },
          });
          this.queryClient.setQueryData(['config', updatedConfig._id], {
            data: { config: updatedConfig },
          });
        }

        console.log('Updated config in cache:', updatedConfig._id);
      }
    }
  }

  // Clear all cache on logout
  clearCache() {
    console.log('Clearing app cache...');

    // Clear React Query cache
    if (this.queryClient) {
      this.queryClient.clear();
    }

    // Clear localStorage
    try {
      localStorage.removeItem(this.cacheKey);
    } catch (error) {
      console.warn('Failed to clear localStorage cache:', error);
    }

    console.log('Cache cleared successfully');
  }

  // Pre-populate React Query with localStorage data
  prePopulateCache(queryClient: QueryClient) {
    const cacheData = this.getCacheData();
    if (cacheData?.configs) {
      // Set the configs data in React Query
      queryClient.setQueryData(['configs'], {
        data: {
          configs: cacheData.configs,
          total: cacheData.configs.length,
        },
      });

      // Pre-populate individual config queries
      cacheData.configs.forEach((config: any) => {
        queryClient.setQueryData(['config', config._id], {
          data: { config },
        });
        console.log('Pre-populated config:', config._id);
      });
    }
  }

  // Check if cache is fresh (less than 24 hours)
  isCacheFresh(): boolean {
    const cacheData = this.getCacheData();
    if (!cacheData) return false;

    const age = Date.now() - cacheData.lastUpdated;
    return age < 24 * 60 * 60 * 1000; // 24 hours
  }
}

// Export singleton instance
export const cacheService = new CacheService();
